import React from 'react';
import { Hit } from '@algolia/client-search';
import { Button, Card, Table } from '@avila-tek/ui/src';
import { createColumnHelper } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import {
  ApolloQueryResult,
  OperationVariables,
  useMutation,
  useQuery,
} from '@apollo/client';
import { Benefit, ClientGoals, Company, User } from '../../../models';
import {
  useAssignPeriod,
  useGoalsDashboard,
  useNotify,
  useUser,
} from '../../../hooks';
import { PAY_REWARD_CLIENT_GOAL } from '../../../graphql/mutation';
import { GET_BENEFIT, GET_COMPANY_POINTS } from '../../../graphql/queries';
import { findItemByText } from '../../../lib';
import Alert from '../Alert/Alert';

interface UserWithGoalId extends Partial<User> {
  clientGoalId: string;
  percentage: string;
  result: number;
  rewarded: number;
  alreadyRewarded: number;
}

const columnHelper = createColumnHelper<User>();

interface SelectEmployeeProps {
  users: User[];
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  data: UserWithGoalId[];
  check: boolean;
  refetch?: (variables?: Partial<OperationVariables>) => Promise<
    ApolloQueryResult<{
      clientGoals: ClientGoals[];
    }>
  >;
}

function SelectEmployee({
  users,
  setUsers,
  data,
  check,
  refetch,
}: SelectEmployeeProps) {
  const router = useRouter();
  const [user] = useUser();
  const notify = useNotify();
  const { period } = useAssignPeriod();
  const goalDashboard = useGoalsDashboard(user.company as Company, {});

  const [loading, setLoading] = React.useState<boolean>(false);
  const [periodsClientsData, setPeriodsCalientsData] =
    React.useState<UserWithGoalId[]>(data);
  const [searchText, setSearchText] = React.useState<string | number>('');

  const columns = [
    ...(check
      ? [
          {
            header: 'Seleccionar',
            cell: (info) => {
              const employee = info.row.original as User;
              const isChecked = users.some((u) => u._id === employee._id);
              return (
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleCheck(employee as Hit<User>)}
                  className="w-4 h-4 mt-1 cursor-pointer bg-white rounded border-purple-200 focus:ring-purple-300 focus:ring-2"
                  disabled={loading}
                />
              );
            },
          },
        ]
      : []),

    columnHelper.accessor('firstName', {
      header: 'Nombre',
      cell: (info) =>
        `${info.row.original.firstName} ${info.row.original.lastName}`,
    }),

    columnHelper.accessor('email', {
      header: 'Correo',
      cell: (info) => `${info.row.original.email}`,
    }),

    ...(!check
      ? [
          {
            header: 'Porcentaje',
            cell: (info) => `${info.row.original.percentage}`,
          },
          {
            header: 'Resultados',
            cell: (info) => `${info.row.original.result}`,
          },
          {
            header: 'Recompensa',
            cell: (info) => `${info.row.original.rewarded}`,
          },
          {
            header: 'Recompensado',
            cell: (info) => {
              const { alreadyRewarded } = info.row.original;
              return parseInt(alreadyRewarded) > 0 ? '✔️' : '❌';
            },
          },
        ]
      : []),
  ];

  const { data: dataGetCompanyBenefitInfo } = useQuery<{
    benefit: Benefit;
  }>(GET_BENEFIT, {
    variables: {
      filter: {
        company: (user.company as Company)._id,
      },
    },
  });

  const { data: dataGetBenefit } = useQuery<{
    getCompanyTotalPoints: {
      totalPoints: number;
    };
  }>(GET_COMPANY_POINTS, {
    variables: {
      data: {
        companyId: (user?.company as Company)._id,
      },
    },
  });

  const [payRewardFunction] = useMutation<{
    payRewardClientGoal: ClientGoals;
  }>(PAY_REWARD_CLIENT_GOAL);

  const handleCheck = (employee: Hit<User>) => {
    if (loading) return; // Prevent checking/unchecking while loading

    const userInList = users.find((u) => u._id === employee._id);

    if (userInList) {
      setUsers(users.filter((u) => u._id !== employee._id));
    } else {
      setUsers([...users, employee]);
    }
  };

  const onClickAssignPeriodTarget = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (loading) return; // Prevent action while loading

    e.preventDefault();
    const rowIndex = Array.from(
      e.currentTarget.parentNode?.children || []
    ).indexOf(e.currentTarget);
    if (data[rowIndex].percentage === '100%') {
      notify(
        `El beneficiari@ ${data[rowIndex].firstName} ${data[rowIndex].lastName} ya ha completado su meta`,
        'success'
      );
      return;
    }
    router.push(
      `/app/periods/assignTarget/${period?._id}?clientGoalId=${data[rowIndex].clientGoalId}`
    );
  };

  const onClickPayRewards = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    try {
      const beneficiariesToPay = data.filter(
        ({ result, alreadyRewarded }) => result > 0 && alreadyRewarded === 0
      );

      if (!beneficiariesToPay.length) {
        return notify('No hay recompensas por pagar', 'error');
      }

      const totalPointsToPay = beneficiariesToPay.reduce(
        (sum, { rewarded }) => sum + rewarded,
        0
      );

      const availablePoints =
        (dataGetBenefit?.getCompanyTotalPoints.totalPoints ?? 0) /
        dataGetCompanyBenefitInfo.benefit.value;

      if (availablePoints >= totalPointsToPay) {
        notify('Serán pagadas las recompensas con puntos prepagados', 'info');
        setLoading(true);

        setTimeout(async () => {
          const { data: dataPayRewards } = await payRewardFunction({
            variables: {
              data: {
                companyId: (user.company as Company)._id,
                periodId: period._id,
                isShopCart: false,
              },
            },
          });

          notify(
            dataPayRewards.payRewardClientGoal
              ? 'Han sido asignadas las recompensas con éxito'
              : 'Ha ocurrido un error al asignar las recompensas',
            dataPayRewards.payRewardClientGoal ? 'success' : 'error'
          );
          setLoading(false);
          refetch?.();
          goalDashboard.totalPoints.refetchCompayPoints();
        }, 2000);
        return;
      }

      notify(
        'Por no tener puntos prepagados suficientes, se enviará la solicitud al carrito de compras',
        'info'
      );
      setLoading(true);

      setTimeout(async () => {
        const { data: dataPayRewards } = await payRewardFunction({
          variables: {
            data: {
              companyId: (user.company as Company)._id,
              periodId: period._id,
            },
          },
        });

        notify(
          dataPayRewards.payRewardClientGoal
            ? 'Han sido procesadas las recompensas con éxito'
            : 'Ha ocurrido un error al procesar las recompensas',
          dataPayRewards.payRewardClientGoal ? 'success' : 'error'
        );
        setLoading(false);
        refetch?.();
      }, 2000);
    } catch (error) {
      notify('Ha ocurrido un error al pagar las recompensas', 'error');
    }
  };

  const memoizedSyncData = React.useCallback(async () => {
    if (searchText) {
      setPeriodsCalientsData(
        findItemByText(data, searchText as string) as Hit<UserWithGoalId>[]
      );
    } else {
      setPeriodsCalientsData(data);
    }
  }, [searchText, data]);

  const onChange = () => {
    let userData: User[] = [];
    if (users?.length !== data?.length) {
      userData = data as User[];
    }
    setUsers(userData);
  };

  React.useEffect(() => {
    memoizedSyncData();
  }, [searchText, memoizedSyncData]);

  return (
    <Card className="overflow-x-auto p-5">
      <Alert
        title=""
        text="No se tienen beneficiarios asignados a este período"
        show={false}
      />
      <Table<UserWithGoalId>
        data={periodsClientsData?.length > 0 ? periodsClientsData : []}
        columns={columns as any}
        href="/app/goals"
        manualPagination={false}
      >
        <Table.Header>
          <Table.SearchInput
            manualFilter
            value={searchText.toString()}
            onChange={(value) => {
              setSearchText(value);
            }}
          />
          {!check && (
            <Button
              className="w-[50% sm:w-[40%] md:w-[30%]"
              onClick={onClickPayRewards}
              disabled={loading}
            >
              {loading ? 'Aprobando...' : 'Aprobar recompensas'}
            </Button>
          )}
        </Table.Header>
        {check && data?.length > 0 ? (
          <div className="relative">
            <input
              type="checkbox"
              checked={users?.length === data?.length}
              onChange={onChange}
              className="w-4 h-4 mt-1 cursor-pointer bg-white rounded border-purple-200 focus:ring-purple-300 focus:ring-2 absolute top-2 left-2"
            />
          </div>
        ) : null}
        <div className={`${check ? 'ml-3' : ''}`}>
          <Table.Content<User>
            onClickRow={!check ? onClickAssignPeriodTarget : null}
          />
        </div>
        <Table.Footer>
          <Table.Pagination className="ml-auto" />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default SelectEmployee;
